import * as React from 'react';
import { HelpIconWithTooltip } from '../../../shared/components/feedback';

interface Props {
  stripe_plans: Array<StripePlan>;
  create_quote_path: string;
  form_authenticity_token: string;
  job_id: string;
  publish_job_path: string;
  job_path: string;
  priority: boolean;
  email_blast: boolean;
  spotlight: boolean;
}

interface StripePlan {
  id: number;
  name: string;
  stripe_plan: string;
  features: string;
  price: string;
  upgrade_text: string;
  upgrade: boolean;
}

export default function JobUpgrade({
  stripe_plans,
  create_quote_path,
  form_authenticity_token,
  job_id,
  publish_job_path,
  job_path,
  priority,
  email_blast,
  spotlight
}: Props) {
  const current_upgrades = { priority, email_blast, spotlight };

  // Default "Candidate Auto Match" to checked
  const [selected_plans, setSelectedPlans] = React.useState<Array<StripePlan>>([
    stripe_plans.find(plan => plan.name.includes('Auto Match'))
  ]);

  // Toggle selected plans
  const toggleSelectedPlan = (stripe_plan: StripePlan) => {
    const { id, upgrade } = stripe_plan;

    // Return if Auto Match is selected unless it is not included in selected_plans
    if (upgrade && stripe_plan.name.includes('Auto Match') && selected_plans.find(plan => plan.name.includes('Auto Match'))) {
      return;
    }

    // If all options are selected, clear out array and select the Bundle plan
    if (upgrade && selected_plans.length === stripe_plans.length - 1) {
      setSelectedPlans([stripe_plans.find(plan => plan.name.includes('Bundle'))]);
      return;
    }

    // Clear out array and add the new plan if the plan name includes Bundle and bundle is not already selected
    if (stripe_plan.name.includes('Bundle') && !selected_plans.find(plan => plan.name.includes('Bundle'))) {
      if (upgrade) {
        setSelectedPlans([stripe_plan]);
        return;
      } else {
        return;
      }
    }

    // If the plan is already selected, remove it and remove Bundle if it's selected
    if (selected_plans.find(plan => plan.id === id)) {
      // If the Bundle plan is selected, remove it
      if (upgrade && selected_plans.find(plan => plan.name.includes('Bundle'))) {
        setSelectedPlans(selected_plans.filter(plan => plan.name.includes('Bundle') === false));
      }
      // Remove the plan
      setSelectedPlans(selected_plans.filter(plan => plan.id !== id));
    } else {
      // Otherwise, add it
      if (upgrade || !stripe_plan.name.includes('Bundle')) {
        setSelectedPlans([...selected_plans, stripe_plan]);
      }
    }
  };

  // UseEffect: If all upgrade options are selected, clear out array and select the Bundle plan, but leave the non-upgrade plans selected
  React.useEffect(() => {
    // Get all upgrade plans and check if they are all selected
    const upgradePlans = stripe_plans.filter(plan => plan.upgrade && !plan.name.includes('Bundle'));
    const allUpgradePlansSelected = upgradePlans.every(upgradePlan =>
      selected_plans.some(selectedPlan => selectedPlan.id === upgradePlan.id)
    );

    // If all upgrade plans are selected, select the Bundle plan and make sure to keep any non-upgrade plans
    if (allUpgradePlansSelected) {
      setSelectedPlans([
        ...selected_plans.filter(plan => !plan.upgrade),
        stripe_plans.find(plan => plan.name.includes('Bundle'))
      ]);
    }
  }, [selected_plans, stripe_plans]);

  // Total Price
  const total_price = selected_plans.reduce((total, plan) => {
    return total + parseFloat(plan.price);
  }, 0);

  // Your upgrade price is $
  const upgrade_string = selected_plans.length > 0
    ? `Your add-on price total is $${total_price}`
    : '';

  // Bundle Selected?
  const bundle_selected = selected_plans.find(plan => plan.name.includes('Bundle'));

  // stripe_plans where upgrade is true
  const upgrade_plans = stripe_plans.filter(plan => plan.upgrade);

  // stripe_plans where upgrade is false
  const non_upgrade_plans = stripe_plans.filter(plan => !plan.upgrade);

  const containerRef = React.useRef<HTMLDivElement>(null);

  const [isSticky, setIsSticky] = React.useState(false);

  const checkStickiness = () => {
    // Check to see if containerRef is in view
    const container = containerRef.current;
    if (!container) return;
    
    const containerTop = container.getBoundingClientRect().top;
    const containerBottom = container.getBoundingClientRect().bottom;
    const containerHeight = container.getBoundingClientRect().height;
    const windowHeight = window.innerHeight;

    // If the container is in view, set isSticky to true
    if (containerTop >= 0 && containerBottom <= windowHeight) {
      setIsSticky(true);
    } else {
      setIsSticky(false);
    }
  };

  React.useEffect(() => {
    window.addEventListener('scroll', checkStickiness);
    return () => {
      window.removeEventListener('scroll', checkStickiness);
    };
  }, []);

  return (
    <>
      <div className="container pb-5 mb-5">
        {/* Header */}
        <div className="text-center mb-4">
          <div className="alert alert-info d-inline-block mb-3" style={{ borderRadius: '8px' }}>
            <i className="fas fa-star me-2"></i>
            <strong>New for Essential Members!</strong>
          </div>
          <h2 className="mb-2" style={{ color: '#0f4b89', fontWeight: 700 }}>
            <i className="fas fa-rocket me-2"></i>
            Upgrade Your Job Posting
          </h2>
          <p className="text-muted" style={{ fontSize: '1.1rem' }}>
            Check out these optional add-ons to help your job reach more candidates!
          </p>
        </div>

        <div className="row justify-content-center">
          <div className="col-12 col-lg-8">
            {/* Upgrade Add-Ons */}
            <div className="content-card mb-4">
              <div className="content-card__header">
                <h3><i className="fas fa-bolt me-2"></i>Job Add-Ons</h3>
              </div>
              <div className="content-card__body">
                {upgrade_plans.map((stripe_plan, index) => (
                  <StripePlan
                    key={stripe_plan.id}
                    stripe_plan={stripe_plan}
                    onClick={() => toggleSelectedPlan(stripe_plan)}
                    selected_plans={selected_plans}
                    bundle_selected={bundle_selected}
                  />
                ))}
              </div>
            </div>

            {/* Recruiter Services */}
            <div className="content-card mb-4">
              <div className="content-card__header">
                <h3><i className="fas fa-users me-2"></i>Recruiter Services</h3>
                <span className="badge-modern badge-modern--info">More Ways to Hire</span>
              </div>
              <div className="content-card__body">
                {non_upgrade_plans.map((stripe_plan, index) => (
                  <StripePlan
                    key={stripe_plan.id}
                    stripe_plan={stripe_plan}
                    onClick={() => toggleSelectedPlan(stripe_plan)}
                    selected_plans={selected_plans}
                    bundle_selected={bundle_selected}
                  />
                ))}
              </div>
            </div>
          </div>
        </div>

        <div ref={containerRef}></div>
      </div>

      {/* Sticky Bottom Actions - Full Width */}
      {total_price > 0 && (
        <div
          className={`sticky-bottom bg-white p-4 text-center border-top ${!isSticky ? 'shadow' : ''}`}
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            width: '100%',
            zIndex: 1000
          }}
        >
          <div className="container">
            <div className="mb-3">
              <span className="h4 fw-bold" style={{ color: '#0f4b89' }}>
                <i className="fas fa-tag me-2"></i>
                {upgrade_string}
              </span>
            </div>

            <form action={create_quote_path} method="post">
              <input type="hidden" name="authenticity_token" value={form_authenticity_token} />
              <input type="hidden" name="job_id" value={job_id} />
              <input type="hidden" name="return_path" value={publish_job_path} />
              <input type="hidden" name="purchase_name" value="Purchase - Job Upgrade" />
              <input type="hidden" name="purchase_amount" value={total_price} />

              {selected_plans.filter((plan) => plan.stripe_plan !== '').map((plan, index) => (
                <input key={index} type="hidden" name="prices[]" value={plan.stripe_plan} />
              ))}

              <button className="btn btn-primary btn-rounded" style={{ fontSize: '1.1rem', padding: '0.75rem 2rem' }}>
                <i className="fas fa-shopping-cart me-2"></i>
                Checkout and Publish
              </button>
            </form>
          </div>
        </div>
      )}

      {total_price === 0 && (
        <div
          className={`sticky-bottom bg-white p-4 text-center border-top ${!isSticky ? 'shadow' : ''}`}
          style={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            width: '100%',
            zIndex: 1000
          }}
        >
          <div className="container">
            <button
              onClick={() => publish_job_path && window.location.replace(publish_job_path)}
              className="btn btn-primary btn-rounded"
              style={{ fontSize: '1.1rem', padding: '0.75rem 2rem' }}
            >
              <i className="fas fa-paper-plane me-2"></i>
              No Thanks, Publish Job Without Add-Ons
            </button>
          </div>
        </div>
      )}
    </>
  );
}

interface StripePlanProps {
  stripe_plan: StripePlan;
  onClick: () => void;
  selected_plans: Array<StripePlan>;
  bundle_selected?: StripePlan;
}

function StripePlan({
  stripe_plan,
  onClick,
  selected_plans,
  bundle_selected
}: StripePlanProps) {
  const { upgrade_text } = stripe_plan;

  // Get text between [ and ] in upgrade_text
  const upgrade_text_match = upgrade_text.match(/\[(.*?)\]/);

  // Upgrade text - remove text between [ and ]
  const upgrade_text_without_match = upgrade_text.replace(/\[(.*?)\]/, '');

  // Run onClick only if !bundle_selected and stripe_plan.name does not include Bundle
  // If the plan is not selected, select the plan
  const onClickWrapper = () => {
    if (!bundle_selected) {
      onClick();
    }
    // If the plan name includes Bundle, select the Bundle plan
    if (stripe_plan.name.includes('Bundle')) {
      onClick();
    }

    // If the plan is not an upgrade, select the plan
    if (!stripe_plan.upgrade) {
      onClick();
    }
  };

  // Opacity of 80% if bundle is selected
  const opacity = bundle_selected && !stripe_plan.name.includes('Bundle') && stripe_plan.upgrade
    ? '50%'
    : '100%';

  // Price is 0
  const price_is_zero = stripe_plan.price == "0";

  return (
    <div
      className="mb-3 p-3 rounded"
      style={{
        opacity,
        cursor: 'pointer',
        border: selected_plans.includes(stripe_plan) ? '2px solid #085ec8' : '2px solid #e8ecef',
        background: selected_plans.includes(stripe_plan) ? 'rgba(8, 94, 200, 0.05)' : 'white',
        transition: 'all 0.2s ease'
      }}
      onClick={onClickWrapper}
      onMouseEnter={(e) => {
        if (!selected_plans.includes(stripe_plan)) {
          e.currentTarget.style.borderColor = 'rgba(8, 94, 200, 0.3)';
          e.currentTarget.style.background = 'rgba(8, 94, 200, 0.02)';
        }
      }}
      onMouseLeave={(e) => {
        if (!selected_plans.includes(stripe_plan)) {
          e.currentTarget.style.borderColor = '#e8ecef';
          e.currentTarget.style.background = 'white';
        }
      }}
    >
      <div className="d-flex align-items-start gap-3">
        {/* Checkbox */}
        {!bundle_selected && (
          <input
            className="form-check-input mt-1"
            type="checkbox"
            value=""
            id={`upgrade-${stripe_plan.id}`}
            checked={selected_plans.includes(stripe_plan)}
            style={{ width: '24px', height: '24px', borderRadius: '6px', flexShrink: 0 }}
            readOnly
          />
        )}
        {bundle_selected && (
          <input
            className="form-check-input mt-1"
            type="checkbox"
            value=""
            id={`upgrade-${stripe_plan.id}`}
            checked={stripe_plan.upgrade ? true : selected_plans.includes(stripe_plan)}
            style={{ width: '24px', height: '24px', borderRadius: '6px', flexShrink: 0 }}
            readOnly
          />
        )}

        {/* Content */}
        <div className="flex-grow-1">
          <div className="d-flex justify-content-between align-items-start mb-1">
            <h5 className="mb-0" style={{ color: '#0f4b89', fontWeight: 600 }}>
              {stripe_plan.name}
            </h5>
            <div className="d-flex align-items-center gap-2">
              <span className="badge-modern badge-modern--success" style={{ fontSize: '1rem', fontWeight: 700 }}>
                {(stripe_plan.price != "0") ? `$${stripe_plan.price}` : 'Included'}
              </span>
              <HelpIconWithTooltip message={stripe_plan.name} />
            </div>
          </div>
          <p className="text-muted mb-0" style={{ fontSize: '0.9rem' }}>
            {upgrade_text_without_match}
          </p>
          {upgrade_text_match && upgrade_text_match[1] && (
            <p className="text-danger mb-0 mt-1" style={{ fontSize: '0.85rem', fontWeight: 600 }}>
              <i className="fas fa-star me-1"></i>
              {upgrade_text_match[1]}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
